import { useState, useRef, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { MODEL_PORTFOLIOS, compareToModel, generateSuggestions, findClosestModel } from '../utils/modelPortfolios';
import { getAssetClassColor } from '../utils/assetClasses';
import Tooltip from './Tooltip';

// Abbreviate long asset class names for chart
const abbreviateAssetClass = (name) => {
  const abbreviations = {
    'US Stocks': 'US Stocks',
    'International Stocks': 'Intl Stocks',
    'Emerging Markets': 'Emerging',
    'Bonds': 'Bonds',
    'Gold/Commodities': 'Gold/Comm',
    'Cash': 'Cash',
    'Real Estate': 'Real Estate',
    'Other': 'Other'
  };
  return abbreviations[name] || name;
};

// Risk level badge classes
const getRiskBadgeClass = (riskLevel) => {
  switch (riskLevel) {
    case 'Conservative': return 'bg-gain-bg text-gain';
    case 'Moderate': return 'bg-muted text-muted-foreground';
    case 'Aggressive': return 'bg-loss-bg text-loss';
    default: return 'bg-muted text-muted-foreground';
  }
};

// Custom label renderer for bars (declared outside component to avoid re-creation)
const renderBarLabel = (props) => {
  const { x, y, width, value } = props;
  if (value < 3) return null; // Don't show labels for very small values
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="var(--muted-foreground)"
      textAnchor="middle"
      fontSize={10}
      fontWeight={500}
    >
      {value.toFixed(0)}%
    </text>
  );
};

// Custom tooltip for chart (declared outside component to avoid re-creation)
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const fullName = payload[0]?.payload?.fullName || label;
    const diff = payload[0]?.payload?.difference;
    return (
      <div className="bg-card border border-border rounded-md p-3 shadow-md">
        <p className="font-semibold text-foreground mb-2 text-sm">{fullName}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs text-muted-foreground">
            <span className="font-medium" style={{ color: entry.color }}>
              {entry.name}:
            </span>{' '}
            {entry.value.toFixed(1)}%
          </p>
        ))}
        {diff !== 0 && (
          <p className={`text-xs font-semibold mt-2 pt-2 border-t border-border ${diff > 0 ? 'text-gain' : 'text-loss'}`}>
            Difference: {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
          </p>
        )}
      </div>
    );
  }
  return null;
}

function PortfolioComparison({ groupedPositions }) {
  // Find closest model automatically
  const closestModelInfo = findClosestModel(groupedPositions);
  const [selectedModel, setSelectedModel] = useState(closestModelInfo?.key || '60-40-classic');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const comparison = compareToModel(groupedPositions, selectedModel);
  const suggestions = generateSuggestions(comparison);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (!comparison) return null;

  // Prepare data for bar chart with abbreviated names
  const chartData = comparison.allAssetClasses.map(assetClass => ({
    assetClass: abbreviateAssetClass(assetClass),
    fullName: assetClass,
    'Your Portfolio': comparison.userAllocations[assetClass] || 0,
    [comparison.model.name]: comparison.modelAllocations[assetClass] || 0,
    difference: comparison.differences[assetClass] || 0
  }));

  const selectedModelData = MODEL_PORTFOLIOS[selectedModel];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header Section */}
      <div className="px-6 py-5 border-b border-border bg-muted">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <svg className="w-5 h-5 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                Compare to Model Portfolios
                <Tooltip text="Compare your portfolio to popular investment strategies like 3-fund portfolio, 60/40 classic, or all-weather allocation." />
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                See how your allocation compares to proven investment strategies
              </p>
            </div>
          </div>

          {/* Custom Dropdown */}
          <div className="relative w-full lg:w-72" ref={dropdownRef}>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
              Model Portfolio
            </label>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`relative w-full px-4 py-3 bg-background border rounded-lg text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                isDropdownOpen ? 'border-foreground' : 'border-input hover:border-foreground/50'
              }`}
            >
              <span className="block font-medium text-foreground truncate">
                {selectedModelData.name}
              </span>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-md ${getRiskBadgeClass(selectedModelData.riskLevel)}`}>
                {selectedModelData.riskLevel}
              </span>

              <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute z-50 mt-2 w-full max-h-72 overflow-auto rounded-lg bg-card border border-border shadow-lg">
                {Object.entries(MODEL_PORTFOLIOS).map(([key, model]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setSelectedModel(key);
                      setIsDropdownOpen(false);
                    }}
                    className={`relative w-full px-4 py-3 text-left transition-colors hover:bg-muted focus:outline-none focus:bg-muted border-b border-border last:border-0 ${
                      key === selectedModel ? 'bg-muted border-l-2 border-l-foreground' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`block font-medium text-sm ${key === selectedModel ? 'text-foreground' : 'text-foreground'}`}>
                          {model.name}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {model.description}
                        </span>
                        <span className={`inline-block mt-1.5 px-2 py-0.5 text-xs font-medium rounded-md ${getRiskBadgeClass(model.riskLevel)}`}>
                          {model.riskLevel}
                        </span>
                      </div>

                      {key === selectedModel && (
                        <svg className="w-5 h-5 text-foreground flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Closest Match Banner */}
      {closestModelInfo && closestModelInfo.key === selectedModel && (
        <div className="mx-6 mt-4 flex items-center gap-3 px-4 py-3 bg-gain-bg border border-gain/30 rounded-lg">
          <div className="p-1.5 bg-gain-bg rounded-md border border-gain/30">
            <svg className="w-4 h-4 text-gain" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gain">
              Best Match for Your Portfolio
            </p>
            <p className="text-xs text-gain/70">
              Only {closestModelInfo.difference.toFixed(1)}% difference from this model
            </p>
          </div>
        </div>
      )}

      {/* Chart Section */}
      <div className="p-6">
        <div className="bg-muted border border-border rounded-lg p-4 md:p-6">
          <h4 className="text-xs font-medium text-muted-foreground mb-4 text-center uppercase tracking-wide">
            Asset Class Allocation Comparison
          </h4>

          <div className="w-full" style={{ minHeight: '320px' }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={chartData}
                margin={{ top: 25, right: 20, left: 0, bottom: 5 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="assetClass"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                  axisLine={{ stroke: 'var(--border)' }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `${value}%`}
                  width={45}
                />
                <ChartTooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.5 }} />
                <Legend
                  wrapperStyle={{ paddingTop: '16px' }}
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => <span className="text-sm ml-1" style={{ color: 'var(--muted-foreground)' }}>{value}</span>}
                />
                <Bar
                  dataKey="Your Portfolio"
                  fill="var(--foreground)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                >
                  <LabelList dataKey="Your Portfolio" content={renderBarLabel} />
                </Bar>
                <Bar
                  dataKey={comparison.model.name}
                  fill="var(--muted-foreground)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                >
                  <LabelList dataKey={comparison.model.name} content={renderBarLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Model Info & Suggestions Grid */}
      <div className="px-6 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Model Portfolio Details */}
        <div className="bg-muted border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Model Breakdown
            </h4>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            {comparison.model.description}
          </p>

          <div className="grid grid-cols-2 gap-2">
            {Object.entries(comparison.model.allocations).map(([assetClass, percentage]) => (
              <div key={assetClass} className="flex items-center gap-2 text-sm">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getAssetClassColor(assetClass) }}
                />
                <span className="text-muted-foreground truncate">{assetClass}</span>
                <span className="font-semibold text-foreground ml-auto">{percentage}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Key Insights */}
        {suggestions.length > 0 && (
          <div className="bg-muted border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Key Insights
              </h4>
            </div>

            <ul className="space-y-2">
              {suggestions.slice(0, 3).map((suggestion, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="text-muted-foreground mt-0.5">•</span>
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Difference Summary Cards */}
      <div className="px-6 pb-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-muted border border-border rounded-lg p-4 text-center hover:bg-accent transition-colors">
            <div className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">Stocks</div>
            <div className={`text-xl font-bold font-mono ${comparison.stocksDiff > 0 ? 'text-gain' : comparison.stocksDiff < 0 ? 'text-loss' : 'text-foreground'}`} style={{ fontVariantNumeric: 'tabular-nums lining-nums' }}>
              {comparison.stocksDiff > 0 ? '+' : ''}{comparison.stocksDiff.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground mt-1">vs model</div>
          </div>

          <div className="bg-muted border border-border rounded-lg p-4 text-center hover:bg-accent transition-colors">
            <div className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">Bonds</div>
            <div className={`text-xl font-bold font-mono ${comparison.bondsDiff > 0 ? 'text-gain' : comparison.bondsDiff < 0 ? 'text-loss' : 'text-foreground'}`} style={{ fontVariantNumeric: 'tabular-nums lining-nums' }}>
              {comparison.bondsDiff > 0 ? '+' : ''}{comparison.bondsDiff.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground mt-1">vs model</div>
          </div>

          <div className="bg-muted border border-border rounded-lg p-4 text-center hover:bg-accent transition-colors">
            <div className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">Risk</div>
            <div className={`text-base font-bold ${
              comparison.model.riskLevel === 'Conservative' ? 'text-gain' :
              comparison.model.riskLevel === 'Aggressive' ? 'text-loss' :
              'text-muted-foreground'
            }`}>
              {comparison.model.riskLevel}
            </div>
            <div className="text-xs text-muted-foreground mt-1">target level</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortfolioComparison;
